import { useCallback, useRef, useState } from 'react';
import { api } from '@/lib/api';

/**
 * Hook for exporting a note as PDF via POST /api/notes/:id/export-pdf.
 *
 * Tracks in-flight exports per noteId so simultaneous exports of different
 * notes do not block each other.
 *
 * Usage:
 *   const { isExporting, exportPdf } = useExportPdf();
 *   exportPdf(noteId, (msg) => setToast(msg));
 */
export function useExportPdf(): {
  isExporting: (noteId: string) => boolean;
  exportPdf: (noteId: string, notify: (msg: string) => void) => Promise<void>;
} {
  // Use state so components re-render when export state changes
  const [exportingIds, setExportingIds] = useState<Set<string>>(new Set());
  // Ref for synchronous guard inside exportPdf to avoid stale closure
  const exportingRef = useRef<Set<string>>(new Set());

  const isExporting = useCallback(
    (noteId: string) => exportingIds.has(noteId),
    [exportingIds],
  );

  const exportPdf = useCallback(async (noteId: string, notify: (msg: string) => void) => {
    if (exportingRef.current.has(noteId)) return;

    exportingRef.current.add(noteId);
    setExportingIds((prev) => new Set([...prev, noteId]));

    try {
      await api.post(`/api/notes/${noteId}/export-pdf`);
      notify('PDF export started — you will receive it by email shortly.');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Export failed. Please try again.';
      notify(message);
    } finally {
      exportingRef.current.delete(noteId);
      setExportingIds((prev) => {
        const next = new Set(prev);
        next.delete(noteId);
        return next;
      });
    }
  }, []);

  return { isExporting, exportPdf };
}
